/**
 * CSK Theorem Registry — canonical catalog of all 10 guarantees.
 *
 * The registry is the TypeScript projection of the canonical theorem
 * metadata. Registry invariant checks live in tests; this module exposes
 * lookup and grouping helpers for runtime consumers.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { GENERATOR_CODES, SEAM_TYPES } from "../foundation/types.js";
import type { GeneratorCode, NeutralizationModeName, SeamType } from "../foundation/types.js";
import {
  NEUTRALIZATION_THEOREM_META,
  type NeutralizationTheoremCode,
  type NeutralizationSecurityGuarantee,
  type NeutralizationTheoremName,
} from "../shared/canonical.js";

// ── Registry ───────────────────────────────────────────────────

/** All 10 guarantee metadata entries, keyed by theorem code. */
export const THEOREM_REGISTRY: ReadonlyMap<
  NeutralizationTheoremCode,
  NeutralizationSecurityGuarantee
> = new Map<NeutralizationTheoremCode, NeutralizationSecurityGuarantee>(
  NEUTRALIZATION_THEOREM_META.map((meta) => [meta.code, meta]),
);

/** Look up a theorem by code. */
export function getTheorem(code: string): NeutralizationSecurityGuarantee | undefined {
  return (THEOREM_REGISTRY as ReadonlyMap<string, NeutralizationSecurityGuarantee>).get(code);
}

/** Get all theorems for a given safety class. */
export function byClass(
  safetyClass: "compositional" | "parametric",
): readonly NeutralizationSecurityGuarantee[] {
  return [...THEOREM_REGISTRY.values()].filter((m) => m.safetyClass === safetyClass);
}

/** Get all theorems for a given neutralization mode. */
export function byMode(mode: NeutralizationModeName): readonly NeutralizationSecurityGuarantee[] {
  return [...THEOREM_REGISTRY.values()].filter((m) => m.mode === mode);
}

/** Get all theorems blocked by a given generator. */
export function byGenerator(gen: GeneratorCode): readonly NeutralizationSecurityGuarantee[] {
  return [...THEOREM_REGISTRY.values()].filter((m) => m.generator === gen);
}

/** Get all theorems at a given tier. */
export function byTier(tier: 1 | 2 | 3): readonly NeutralizationSecurityGuarantee[] {
  return [...THEOREM_REGISTRY.values()].filter((m) => m.tier === tier);
}

/** Get all theorems for a given seam type. */
export function bySeam(seamType: SeamType): readonly NeutralizationSecurityGuarantee[] {
  return [...THEOREM_REGISTRY.values()].filter((m) => m.seamType === seamType);
}

// ── (NT × seam-type) product grid ─────────────────────────────

/** One cell in the (NT × seam-type) product grid. */
export interface ProductGridCell {
  /** Theorem code (e.g., "D1", "P"). */
  readonly code: NeutralizationTheoremCode;
  /** Canonical theorem name. */
  readonly name: NeutralizationTheoremName;
  /** Vulnerability generator (G1–G4). */
  readonly generator: GeneratorCode;
  /** Neutralization mode. */
  readonly mode: NeutralizationModeName;
  /** Seam orientation. */
  readonly seamType: SeamType;
}

/** Result of a product grid completeness check. */
export interface ProductGridResult {
  readonly valid: boolean;
  readonly errors: readonly string[];
  /** Cells grouped by seam type. */
  readonly bySeamType: ReadonlyMap<SeamType, readonly ProductGridCell[]>;
  /** Cells grouped by generator. */
  readonly byGenerator: ReadonlyMap<GeneratorCode, readonly ProductGridCell[]>;
}

/** All four seam orientations that must be inhabited. */
const ALL_SEAM_TYPES: readonly SeamType[] = SEAM_TYPES;

/** All four vulnerability generators that must be covered. */
const ALL_GENERATORS: readonly GeneratorCode[] = GENERATOR_CODES;

/**
 * Verify the (NT × seam-type) product grid is exhaustively covered.
 *
 * Checks that:
 * - Every theorem has exactly one seam type.
 * - All four seam orientations (vertical, horizontal, diagonal, mixed) have
 *   at least one theorem — the BC failure witness for every orientation is
 *   inhabited.
 * - The structural theory constraint holds: all G4/contained theorems must be
 *   horizontal (resource flows cross horizontal boundaries).
 *
 * Agda correspondence:
 *   CSK.Foundation.GaloisConnection.ProductGridCompleteness.all-seams-inhabited
 */
export function checkProductGrid(): ProductGridResult {
  const errors: string[] = [];
  const all = [...THEOREM_REGISTRY.values()];

  // Build seam-type groupings.
  const seamMap = new Map<SeamType, ProductGridCell[]>();
  const genMap = new Map<GeneratorCode, ProductGridCell[]>();

  for (const seam of ALL_SEAM_TYPES) {
    seamMap.set(seam, []);
  }
  for (const gen of ALL_GENERATORS) {
    genMap.set(gen, []);
  }

  for (const m of all) {
    const cell: ProductGridCell = {
      code: m.code,
      name: m.name,
      generator: m.generator,
      mode: m.mode,
      seamType: m.seamType,
    };

    // Validate seam type before inserting so the check is reachable.
    const seamCells = seamMap.get(m.seamType);
    if (seamCells === undefined) {
      errors.push(
        `Theorem ${m.code} has unknown seam type "${m.seamType}"; ` +
          `expected one of: ${ALL_SEAM_TYPES.join(", ")}`,
      );
      // Still track it so it shows up in the output.
      seamMap.set(m.seamType, [cell]);
    } else {
      seamCells.push(cell);
    }

    // Validate generator before inserting.
    const genCells = genMap.get(m.generator);
    if (genCells === undefined) {
      // Unknown generators are tracked but do not cause an error here;
      // the ALL_GENERATORS loop below reports missing coverage.
      genMap.set(m.generator, [cell]);
    } else {
      genCells.push(cell);
    }
  }

  // All four seam orientations must be inhabited (BC failure witness present).
  for (const seam of ALL_SEAM_TYPES) {
    const cells = seamMap.get(seam) ?? [];
    if (cells.length === 0) {
      errors.push(
        `Seam type "${seam}" has no theorems. ` +
          `The BC failure witness for this orientation is not inhabited.`,
      );
    }
  }

  // All generators must be covered.
  for (const gen of ALL_GENERATORS) {
    const cells = genMap.get(gen) ?? [];
    if (cells.length === 0) {
      errors.push(`Generator ${gen} is not covered by any theorem.`);
    }
  }

  // Structural constraint: G4/contained theorems must be horizontal.
  for (const m of all) {
    if (m.generator === "G4" && m.mode === "contained" && m.seamType !== "horizontal") {
      errors.push(
        `Structural constraint: G4/contained theorem ${m.code} must be horizontal ` +
          `(resource flows cross horizontal boundaries), but is "${m.seamType}".`,
      );
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    bySeamType: seamMap,
    byGenerator: genMap,
  };
}

// ── Lane-local BC soundness ────────────────────────────────────

/**
 * Describes a theorem lane's BC enforcement profile.
 *
 * This is a **metadata cross-validation summary**, not a formal soundness
 * verifier. It reports how each theorem's neutralization mode and safety
 * class are recorded in the registry, and flags any structural inconsistency
 * between them.
 *
 * The structural invariant from CSK theory:
 *   - IMMUNE ↔ compositional safety class (D1, D2, P, S, W)
 *   - CONTAINED ↔ parametric safety class (R1, R2)
 *   - INERT   ↔ parametric safety class (C, I1, I2)
 *
 * `modeClassConsistent` is `true` when the above invariant holds for this
 * theorem. This CAN be false if the registry is manually edited incorrectly.
 *
 * Agda correspondence:
 *   CSK.Foundation.GaloisConnection.Seams.BCFailsAt
 *   CSK.Foundation.GaloisConnection.ProductGridCompleteness (§7 NT code mapping)
 */
export interface LaneBCSoundness {
  readonly code: NeutralizationTheoremCode;
  readonly name: NeutralizationTheoremName;
  readonly seamType: SeamType;
  /** How BC failure is enforced in this lane (derived from mode). */
  readonly enforcementMechanism: string;
  /**
   * True when the mode-safetyClass pair is structurally consistent.
   *
   * Structural invariant: IMMUNE ↔ compositional; CONTAINED/INERT ↔ parametric.
   * This is a genuine registry cross-validation — it can fail if the registry
   * is incorrectly edited.
   */
  readonly modeClassConsistent: boolean;
}

/** Result of a lane-local BC enforcement profile check across all theorem lanes. */
export interface BCSoundnessResult {
  readonly valid: boolean;
  readonly errors: readonly string[];
  readonly lanes: readonly LaneBCSoundness[];
}

/** Derive the lane-local enforcement mechanism description from theorem metadata. */
function _laneEnforcement(m: NeutralizationSecurityGuarantee): string {
  if (m.mode === "immune") {
    return `${m.seamType} seam — violation type uninhabited (IMMUNE: no type representation)`;
  }
  if (m.mode === "contained") {
    return `${m.seamType} seam — violation bounded by resource budget (CONTAINED: linear scope)`;
  }
  // inert
  return `${m.seamType} seam — unsafe composition is type-disallowed (INERT: algebraic constraint)`;
}

/**
 * Validate the lane-local BC enforcement profile for every theorem in the registry.
 *
 * This cross-validates two orthogonal metadata fields for each theorem:
 *   1. `mode` (immune / contained / inert) — determines the neutralization mechanism
 *   2. `safetyClass` (compositional / parametric) — determines composition safety
 *
 * The structural invariant that must hold:
 *   - `mode === "immune"` ↔ `safetyClass === "compositional"`
 *   - `mode === "contained" || mode === "inert"` ↔ `safetyClass === "parametric"`
 *
 * Violations indicate registry inconsistency and are reported as errors.
 * Each lane also reports its enforcement mechanism description for audit.
 *
 * Agda correspondence:
 *   CSK.Foundation.GaloisConnection.ProductGridCompleteness.NTCode
 *     orientation-of (§7) — the NT-code-to-orientation mapping this validates
 */
export function checkBCSoundness(): BCSoundnessResult {
  const errors: string[] = [];
  const lanes: LaneBCSoundness[] = [];

  for (const m of THEOREM_REGISTRY.values()) {
    // Validate the mode-safetyClass structural invariant.
    // This is a genuine cross-check: IMMUNE must be compositional and vice versa.
    const immuneIsCompositional = m.mode === "immune" ? m.safetyClass === "compositional" : true;
    const containedOrInertIsParametric =
      m.mode === "contained" || m.mode === "inert" ? m.safetyClass === "parametric" : true;
    // Conversely: compositional must be immune, parametric must be contained or inert.
    const compositionalIsImmune = m.safetyClass === "compositional" ? m.mode === "immune" : true;
    const parametricIsContainedOrInert =
      m.safetyClass === "parametric" ? m.mode === "contained" || m.mode === "inert" : true;

    const modeClassConsistent =
      immuneIsCompositional &&
      containedOrInertIsParametric &&
      compositionalIsImmune &&
      parametricIsContainedOrInert;

    if (!modeClassConsistent) {
      errors.push(
        `Theorem ${m.code} (${m.name}): mode "${m.mode}" is inconsistent with ` +
          `safetyClass "${m.safetyClass}". ` +
          `Structural invariant: IMMUNE ↔ compositional, CONTAINED/INERT ↔ parametric.`,
      );
    }

    lanes.push({
      code: m.code,
      name: m.name,
      seamType: m.seamType,
      enforcementMechanism: _laneEnforcement(m),
      modeClassConsistent,
    });
  }

  return {
    valid: errors.length === 0,
    errors,
    lanes,
  };
}
